import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Card, CardContent, Chip, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Stack, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { getUsersByFilter, utenteEliminaRipristina } from "../../services/usersServices";
import ConfirmationModal from "../Shared/confirmationModal";
import { useUI } from "../Context/UIContext";
import TableLoadingOverlay from "../Shared/TableLoadingOverlay";
import AdminMergedTable from "../Shared/AdminMergedTable";
import AdminTablePagination from "../Shared/AdminTablePagination";
import AdminPageHeader, { adminPageShellSx } from "../Shared/AdminPageHeader";
import ActionIconButton from "../Shared/ActionIconButton";
import ScrollTopFab from "../Shared/ScrollTopFab";
import { formatValue } from "../../utils/formatters";
import { mergedTableHeadSx, mergedTableRowSx } from "../../utils/adminTableScrollSx";
import { usePermissions } from "../Context/permissionsProvider";

const SESSION_KEY = "users_filters";

const VIS_TUTTI = null;
const VIS_ATTIVI = "ATTIVI";
const VIS_ELIMINATI = "ELIMINATI";

const SORT_REGISTRAZIONE = "REGISTRAZIONE";
const SORT_COGNOME_NOME = "COGNOME_NOME";

const formatDateOnly = (value) => {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
};

const formatNomeCognome = (row) => {
  const ln = row?.lastName == null ? "" : String(row.lastName).trim();
  const fn = row?.firstName == null ? "" : String(row.firstName).trim();
  const combined = [fn, ln].filter(Boolean).join(" ");
  return combined || "-";
};

const isUserDeleted = (row) => row?.isDeleted === 1 || row?.isDeleted === "1";

// Iniziali dell'utente per l'avatar monogramma (nome+cognome, fallback email)
const userInitials = (row) => {
  const fn = (row?.firstName || "").trim();
  const ln = (row?.lastName || "").trim();
  const fromName = `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase();
  if (fromName) return fromName;
  return (row?.email || "?").trim().charAt(0).toUpperCase() || "?";
};

// Avatar monogramma con gradiente (navy per attivi, slate per eliminati)
const MonogramAvatar = ({ label, deleted, size = 52 }) => (
  <Box sx={{
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: deleted
      ? "linear-gradient(145deg, #94a3b8 0%, #64748b 100%)"
      : "linear-gradient(145deg, #0e4a9e 0%, #0b3d87 100%)",
    color: "#ffffff",
    fontWeight: 800,
    fontSize: "1.1rem",
    letterSpacing: "0.02em",
    boxShadow: deleted ? "0 6px 16px rgba(100, 116, 139, 0.25)" : "0 6px 16px rgba(11, 61, 135, 0.22)"
  }}>
    {label}
  </Box>
);

MonogramAvatar.propTypes = {
  label: PropTypes.string,
  deleted: PropTypes.bool,
  size: PropTypes.number
};

// Riga etichetta/valore per le card mobile utenti
const UserInfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, py: 0.7, borderBottom: "1px solid #f1f5f9" }}>
    <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ textAlign: "right", minWidth: 0 }}>{value}</Box>
  </Box>
);

UserInfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node
};

const renderNewsletterStatus = (isSubscribed) => {
  if (isSubscribed) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "48px",
          py: 0.5,
          borderRadius: "8px",
          fontSize: "0.75rem",
          fontWeight: 700,
          backgroundColor: "#e0f2fe", // Soft modern light blue
          color: "#0369a1", // Deep blue/sky text
          border: "1px solid #bae6fd",
          textTransform: "uppercase",
          letterSpacing: "0.03em",
        }}
      >
        Sì
      </Box>
    );
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "48px",
        py: 0.5,
        borderRadius: "8px",
        fontSize: "0.75rem",
        fontWeight: 700,
        backgroundColor: "#f1f5f9", // Neutral slate light gray
        color: "#64748b", // Slate gray text
        border: "1px solid #e2e8f0",
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      No
    </Box>
  );
};

const GestioneUtenti = () => {
  const { isActionVisible } = usePermissions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSuccess, showLoading, showError } = useUI();

  const getInitial = () => {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {};
      }
    }
    return {};
  };

  const initial = getInitial();

  const [filterInput, setFilterInput] = useState(initial.query || "");
  const filterRef = useRef(null);
  const [userList, setUserList] = useState([]);
  const [page, setPage] = useState(initial.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(initial.rowsPerPage || 10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(initial.sortField || SORT_REGISTRAZIONE);
  const [sortDirection, setSortDirection] = useState(initial.sortDirection || "desc");
  const [filterVisibilita, setFilterVisibilita] = useState(
    initial.visibilita === undefined ? VIS_TUTTI : initial.visibilita
  );
  const [tableLoading, setTableLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [pendingRestore, setPendingRestore] = useState(false);

  useEffect(() => {
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        query: filterInput,
        page,
        rowsPerPage,
        sortField,
        sortDirection,
        visibilita: filterVisibilita,
      })
    );
  }, [filterInput, page, rowsPerPage, sortField, sortDirection, filterVisibilita]);

  const fetchData = useCallback(
    async (
      searchQuery = "",
      currentPage = 0,
      currentRows = 10,
      ord = "desc",
      vis = VIS_TUTTI,
      campo = SORT_REGISTRAZIONE
    ) => {
      setTableLoading(true);
      setFetchError(false);
      try {
        const data = await getUsersByFilter(searchQuery, currentPage, currentRows, ord, vis, campo);
        setUserList(data.userSearchResponses || []);
        setTotalCount(Number(data.countUsers) || 0);
      } catch (err) {
        setFetchError(true);
        setErrorMessage(err.message);
      } finally {
        setTableLoading(false);
      }
    },
    []
  );

  const lastParamsRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      const query = filterInput.length >= 3 ? filterInput : "";
      const last = lastParamsRef.current;
      const changed =
        query !== last?.query ||
        page !== last?.page ||
        rowsPerPage !== last?.rowsPerPage ||
        sortDirection !== last?.sortDirection ||
        sortField !== last?.sortField ||
        filterVisibilita !== last?.visibilita;

      if (changed) {
        fetchData(query, page, rowsPerPage, sortDirection, filterVisibilita, sortField);
        lastParamsRef.current = {
          query,
          page,
          rowsPerPage,
          sortDirection,
          sortField,
          visibilita: filterVisibilita,
        };
      }
    }, 300);
    return () => clearTimeout(t);
  }, [fetchData, filterInput, page, rowsPerPage, sortDirection, sortField, filterVisibilita]);

  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  const handleFilter = (e) => {
    e.preventDefault();
    filterRef.current?.blur();
    setPage(0);
  };

  const handleRequestSort = (field) => {
    setPage(0);
    if (sortField === field) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(field === SORT_REGISTRAZIONE ? "desc" : "asc");
    }
  };

  const performAsyncAction = async (actionFn, cleanupFn, skipFetch = false) => {
    try {
      showLoading("Elaborazione in corso...");
      await actionFn();
      if (cleanupFn) cleanupFn();
      showSuccess("Operazione completata con successo!");
      if (!skipFetch) {
        const q = filterInput.length >= 3 ? filterInput : "";
        await fetchData(q, page, rowsPerPage, sortDirection, filterVisibilita, sortField);
      }
    } catch (error) {
      if (cleanupFn) cleanupFn();
      console.error("Async action failed:", error);
      showError(error?.message || "Operazione non riuscita");
    }
  };

  const openConfirm = (row, restore) => {
    setPendingUser(row);
    setPendingRestore(restore);
    setConfirmOpen(true);
  };

  const handleConfirmAction = () => {
    if (!pendingUser) return;
    const id = pendingUser.userListId;
    const restore = pendingRestore;
    setConfirmOpen(false);
    setPendingUser(null);
    void performAsyncAction(
      () => utenteEliminaRipristina({ userListId: id, ripristina: restore }),
      () => { },
      false
    );
  };

  const renderMobileEmptyState = () => (
    <Box
      sx={{
        p: 6,
        textAlign: "center",
        backgroundColor: "#fff",
        borderRadius: 4,
        border: "2px dashed #e0e6ed",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        my: 2,
      }}
    >
      {fetchError ? (
        <>
          <CloudOffIcon sx={{ fontSize: 60, color: "#f44336", opacity: 0.8 }} />
          <Typography variant="h6" sx={{ color: "#1a202c", fontWeight: "bold" }}>
            Servizio non raggiungibile
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            {errorMessage || "Non è stato possibile caricare i dati. Controlla la connessione."}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() =>
              fetchData(
                filterInput.length >= 3 ? filterInput : "",
                page,
                rowsPerPage,
                sortDirection,
                filterVisibilita,
                sortField
              )
            }
            sx={{ backgroundColor: "#0b3d87", borderRadius: 2 }}
          >
            Riprova
          </Button>
        </>
      ) : (
        <>
          <SearchOffIcon sx={{ fontSize: 60, color: "#cbd5e0" }} />
          <Typography variant="h6" sx={{ color: "#1a202c", fontWeight: "bold" }}>
            Nessun risultato trovato
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
            Prova a modificare i filtri di ricerca
          </Typography>
          {filterInput ? (
            <Button
              variant="outlined"
              onClick={() => {
                setFilterInput("");
                setPage(0);
              }}
              sx={{ borderColor: "#0b3d87", color: "#0b3d87", borderRadius: 2 }}
            >
              Pulisci Ricerca
            </Button>
          ) : null}
        </>
      )}
    </Box>
  );

  const renderMobileList = () => (
    <Stack spacing={2} sx={{ mb: 2 }}>
      {userList.map((row) => {
        const id = row.userListId;
        const del = isUserDeleted(row);
        return (
          <Card key={id} elevation={0} sx={{
            borderRadius: "18px",
            border: "1px solid #e6ebf3",
            boxShadow: "0 4px 14px rgba(15, 23, 42, 0.06)",
            overflow: "hidden"
          }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 1.5 } }}>
              {/* Header: avatar monogramma + email + stato */}
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <MonogramAvatar label={userInitials(row)} deleted={del} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    color: "#1e293b",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}>
                    {formatValue(row.email)}
                  </Typography>
                  <Chip
                    label={del ? "Eliminato" : "Attivo"}
                    color={del ? "error" : "success"}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.75, fontWeight: 700 }}
                  />
                </Box>
              </Box>

              {/* Info */}
              <Box sx={{ mt: 1.5 }}>
                <UserInfoRow
                  label="Nome e Cognome"
                  value={<Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#1e293b" }}>{formatNomeCognome(row)}</Typography>}
                />
                <UserInfoRow
                  label="Registrato"
                  value={<Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#1e293b" }}>{formatDateOnly(row.createdAt)}</Typography>}
                />
                <UserInfoRow
                  label="Newsletter"
                  value={renderNewsletterStatus(row.isNewsletterSubscribed)}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1.5,
                  pt: 1.25,
                  borderTop: "1px solid #f0f2f5",
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  {del
                    ? isActionVisible("gestioneUtenti", "riattiva") && (
                      <ActionIconButton
                        onClick={() => openConfirm(row, true)}
                        icon={RestoreIcon}
                        color="#4caf50"
                        mobileBgColor="#f0fff0"
                        isMobile={isMobile}
                        title="Riattiva"
                      />
                    )
                    : isActionVisible("gestioneUtenti", "elimina") && (
                      <ActionIconButton
                        onClick={() => openConfirm(row, false)}
                        icon={DeleteOutlineIcon}
                        color="#f44336"
                        mobileBgColor="#fff0f0"
                        isMobile={isMobile}
                        title="Elimina"
                      />
                    )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );

  const renderMobileCards = () => {
    if (!tableLoading && (fetchError || userList.length === 0)) {
      return renderMobileEmptyState();
    }
    return renderMobileList();
  };

  // Un solo filtro (Stato): mostrato direttamente nella barra, senza dropdown.
  // Su desktop va nell'header (a destra), su mobile in una barra sticky sotto il
  // titolo. Viene montata una sola volta per volta.
  const searchAndFilters = (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        alignItems: "center",
        flexWrap: { xs: "wrap", md: "nowrap" },
        width: { xs: "100%", md: "auto" },
      }}
    >
      <form onSubmit={handleFilter} style={{ flex: "1 1 auto", minWidth: 0 }}>
        <TextField
          fullWidth
          inputRef={filterRef}
          placeholder="Cerca per email, nome o cognome..."
          value={filterInput}
          onChange={(e) => {
            setFilterInput(e.target.value);
            setPage(0);
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#0b3d87", opacity: 0.6 }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "16px",
                backgroundColor: "#f8fafc",
                height: "54px",
                width: { xs: "100%", md: 320 },
                fontSize: "0.9rem",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  backgroundColor: "#f1f5f9",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                },
                "&.Mui-focused": {
                  backgroundColor: "white",
                  boxShadow:
                    "0 0 0 4px rgba(11, 61, 135, 0.08), 0 4px 12px rgba(11, 61, 135, 0.05)",
                  "& fieldset": { borderColor: "#0b3d87 !important", borderWidth: "2px" },
                },
                "& fieldset": { borderColor: "#e2e8f0 !important" },
              },
            },
          }}
        />
      </form>

      <FormControl sx={{ minWidth: 100, flex: { xs: "1 1 5%", md: "0 0 auto" } }}>
        <InputLabel sx={{ color: "#64748b", fontSize: "0.85rem" }}>Stato</InputLabel>
        <Select
          value={filterVisibilita === null ? "" : filterVisibilita}
          onChange={(e) => {
            const v = e.target.value;
            setFilterVisibilita(v === "" ? VIS_TUTTI : v);
            setPage(0);
          }}
          label="Stato"
          sx={{
            borderRadius: "16px",
            backgroundColor: "#f8fafc",
            height: "54px",
            fontSize: "0.85rem",
            transition: "all 0.2s",
            "&:hover": { backgroundColor: "#f1f5f9" },
            "&.Mui-focused": { backgroundColor: "white" },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0 !important" },
          }}
        >
          <MenuItem value="">Tutti</MenuItem>
          <MenuItem value={VIS_ATTIVI}>Attivi</MenuItem>
          <MenuItem value={VIS_ELIMINATI}>Eliminati</MenuItem>
        </Select>
      </FormControl>


      {(filterInput.length >= 3 || filterVisibilita !== null) && (
        <Tooltip title="Pulisci filtri" copyable={false}>
          <IconButton
            onClick={() => {
              setFilterInput("");
              setFilterVisibilita(VIS_TUTTI);
              setSortField(SORT_REGISTRAZIONE);
              setSortDirection("desc");
              setPage(0);
            }}
            sx={{
              flexShrink: 0,
              backgroundColor: "#f1f5f9",
              color: "#475569",
              width: 54,
              height: 54,
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              "&:hover": { backgroundColor: "#e2e8f0" },
              transition: "all 0.2s",
            }}
          >
            <FilterAltOffIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  return (
    <Box sx={adminPageShellSx}>
      <AdminPageHeader
        title="Gestione Utenti"
        subtitle="Visualizza e gestisci gli account degli utenti registrati."
        actions={isMobile ? undefined : searchAndFilters}
      />

      {isMobile && (
        <Box
          sx={{
            position: "sticky",
            top: 15,
            zIndex: 1100,
            mb: 2,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(16px) saturate(180%)",
            WebkitBackdropFilter: "blur(16px) saturate(180%)",
            borderRadius: "18px",
            p: 1,
            boxShadow: "0 8px 24px -10px rgba(31, 38, 135, 0.18)",
          }}
        >
          {searchAndFilters}
        </Box>
      )}

      <Box sx={{ position: "relative", width: "100%", minHeight: "300px" }}>
        <TableLoadingOverlay loading={tableLoading} />

        {isMobile ? (
          renderMobileCards()
        ) : (
          <UsersDesktopTable
            tableLoading={tableLoading}
            userList={userList}
            fetchError={fetchError}
            errorMessage={errorMessage}
            rowsPerPage={rowsPerPage}
            sortField={sortField}
            sortDirection={sortDirection}
            filterInput={filterInput}
            filterVisibilita={filterVisibilita}
            handleRequestSort={handleRequestSort}
            openConfirm={openConfirm}
            fetchData={fetchData}
            setFilterInput={setFilterInput}
            setFilterVisibilita={setFilterVisibilita}
            setPage={setPage}
            headerTop={0}
          />
        )}
      </Box>

      <UsersPagination
        isMobile={isMobile}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        totalCount={totalCount}
      />

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setPendingUser(null);
        }}
        onConfirm={handleConfirmAction}
        title={pendingRestore ? "Ripristinare utente?" : "Eliminare utente?"}
        subtitle=""
        message={
          pendingRestore
            ? `Il profilo con email ${formatValue(pendingUser?.email)} sarà di nuovo attivo sul sito.`
            : `L'utente con email ${formatValue(pendingUser?.email)} non potrà più accedere finché non verrà ripristinato (eliminazione logica).`
        }
        confirmText="CONFERMA"
        cancelText="ANNULLA"
        confirmColor={pendingRestore ? "#4caf50" : "#e00036"}
      />

      <ScrollTopFab />
    </Box>
  );
};

const UsersDesktopTable = ({
  tableLoading,
  userList,
  fetchError,
  errorMessage,
  rowsPerPage,
  sortField,
  sortDirection,
  filterInput,
  filterVisibilita,
  handleRequestSort,
  openConfirm,
  fetchData,
  setFilterInput,
  setFilterVisibilita,
  setPage,
  headerTop,
}) => {
  const { isActionVisible } = usePermissions();

  return (
  <AdminMergedTable
    tableLoading={tableLoading}
    containerSx={{ borderTopLeftRadius: 12, borderTopRightRadius: 12 }}
    head={(
      <TableHead sx={mergedTableHeadSx(headerTop)}>
        <TableRow>
          <TableCell>
            <TableSortLabel
              active={sortField === SORT_COGNOME_NOME}
              direction={sortField === SORT_COGNOME_NOME ? sortDirection : "asc"}
              onClick={() => handleRequestSort(SORT_COGNOME_NOME)}
              sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
            >
              Nome e Cognome
            </TableSortLabel>
          </TableCell>
          <TableCell>Email</TableCell>
          <TableCell>
            <TableSortLabel
              active={sortField === SORT_REGISTRAZIONE}
              direction={sortField === SORT_REGISTRAZIONE ? sortDirection : "desc"}
              onClick={() => handleRequestSort(SORT_REGISTRAZIONE)}
              sx={{ color: "white !important", "& .MuiTableSortLabel-icon": { color: "white !important" } }}
            >
              Registrato
            </TableSortLabel>
          </TableCell>
          <TableCell align="center">Newsletter</TableCell>
          <TableCell>Stato</TableCell>
          <TableCell align="center">Azioni</TableCell>
        </TableRow>
      </TableHead>
    )}
    body={(
      <TableBody>
        {!tableLoading && (fetchError || userList.length === 0) ? (
          <TableRow>
            <TableCell colSpan={6} align="center">
              <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minHeight: "250px" }}>
                {fetchError ? (
                  <>
                    <Box sx={{ backgroundColor: "#fff5f5", p: 2, borderRadius: "50%", display: "flex" }}>
                      <CloudOffIcon sx={{ fontSize: 60, color: "#f44336" }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                      Oops! Qualcosa è andato storto
                    </Typography>
                    <Typography variant="body1" sx={{ color: "#718096", maxWidth: "400px" }}>
                      {errorMessage || "Errore sconosciuto"}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<RefreshIcon />}
                      onClick={() =>
                        fetchData(
                          filterInput.length >= 3 ? filterInput : "",
                          0,
                          rowsPerPage,
                          sortDirection,
                          filterVisibilita,
                          sortField
                        )
                      }
                      sx={{ bgcolor: "#0b3d87" }}
                    >
                      Riprova
                    </Button>
                  </>
                ) : (
                  <>
                    <Box sx={{ backgroundColor: "#f7fafc", p: 2, borderRadius: "50%", display: "flex" }}>
                      <SearchOffIcon sx={{ fontSize: 60, color: "#a0aec0" }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                      Nessun utente trovato
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilterInput("");
                        setFilterVisibilita(VIS_TUTTI);
                        setPage(0);
                      }}
                    >
                      Pulisci filtri
                    </Button>
                  </>
                )}
              </Box>
            </TableCell>
          </TableRow>
        ) : (
          userList.map((row, index) => {
            const del = isUserDeleted(row);
            return (
              <TableRow key={row.userListId} sx={mergedTableRowSx(index)}>
                <TableCell>
                  <Tooltip title={formatNomeCognome(row)}>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        maxWidth: "160px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        verticalAlign: "middle",
                      }}
                    >
                      {formatNomeCognome(row)}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title={row.email || ""}>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        maxWidth: "200px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        verticalAlign: "middle",
                      }}
                    >
                      {formatValue(row.email)}
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>{formatDateOnly(row.createdAt)}</TableCell>
                <TableCell align="center">
                  {renderNewsletterStatus(row.isNewsletterSubscribed)}
                </TableCell>
                <TableCell>
                  <Chip label={del ? "Eliminato" : "Attivo"} color={del ? "error" : "success"} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" gap={1} justifyContent="center">
                    {del
                      ? isActionVisible("gestioneUtenti", "riattiva") && (
                        <ActionIconButton
                          onClick={() => openConfirm(row, true)}
                          icon={RestoreIcon}
                          color="#4caf50"
                          title="Riattiva"
                        />
                      )
                      : isActionVisible("gestioneUtenti", "elimina") && (
                        <ActionIconButton
                          onClick={() => openConfirm(row, false)}
                          icon={DeleteOutlineIcon}
                          color="#f44336"
                          title="Elimina"
                        />
                      )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    )}
  />
  );
};

UsersDesktopTable.propTypes = {
  tableLoading: PropTypes.bool.isRequired,
  userList: PropTypes.array.isRequired,
  fetchError: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  rowsPerPage: PropTypes.number.isRequired,
  sortField: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  filterInput: PropTypes.string.isRequired,
  filterVisibilita: PropTypes.string,
  handleRequestSort: PropTypes.func.isRequired,
  openConfirm: PropTypes.func.isRequired,
  fetchData: PropTypes.func.isRequired,
  setFilterInput: PropTypes.func.isRequired,
  setFilterVisibilita: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  headerTop: PropTypes.number,
};

const UsersPagination = ({ isMobile, rowsPerPage, setRowsPerPage, page, setPage, totalPages, totalCount }) => (
  <AdminTablePagination
    isMobile={isMobile}
    rowsPerPage={rowsPerPage}
    setRowsPerPage={setRowsPerPage}
    page={page}
    setPage={setPage}
    totalPages={totalPages}
    countLabel="Utenti"
    countValue={totalCount}
  />
);

UsersPagination.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
};

export default GestioneUtenti;
